import classNames from 'classnames';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import Pricing from '@/vdp/Pricing';
import Tags from '@/vdp/Tags';
import HeroBadge from '~/components/vdp/HeroHeader/HeroBadge';
import { useIsMobile } from '~/context/common/DeviceContext';
import DiscountContext from '~/context/common/DiscountContext';
import TradeContext from '~/context/common/TradeContext';
import { useMakeModelSearchLink } from '~/hooks/vdp';
import useVehicleNotifyStatus from '~/hooks/vdp/cta/useVehicleNotifyStatus';
import useIsInSoftNotifyExperiment from '~/hooks/vdp/useInSoftNotifyExperiment';
import useShouldShowStatusDataForExperiment from '~/hooks/vdp/useShouldShowStatusDataForExperiment';
import {
  getCtaButtonState,
  getIsLite,
  getIsPartnerVehicle,
  getShowRecentlyAddedBadge,
  getVehicleDetails,
  useIsPriceLoading
} from '~/selectors';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import {
  FirstRow,
  GetStarted,
  PriceAndButtons,
  TrimMileageBadge,
  VehicleDescription,
  ViewMore,
  Wrapper,
  YearMakeModel
} from './HeroHeaderLite.styles';
import { RecentlyAddedBadge } from '../Badges';

function HeroHeaderLite() {
  const { hasDiscount, totalDiscount, tradeIncentive } =
    useContext(DiscountContext);
  const { isTradeInApplied } = useContext(TradeContext);
  const isPriceLoading = useIsPriceLoading(DiscountContext, TradeContext);
  const viewMoreCarsLink = useMakeModelSearchLink();
  const { isDisabled, notifyMe } = useSelector(getCtaButtonState);
  const {
    make,
    mileage = 0,
    model,
    previousPrice = 0,
    price = 0,
    trim,
    year
  } = useSelector(getVehicleDetails);
  const isPartnerVehicle = useSelector(getIsPartnerVehicle);
  const shouldShowViewMoreCars = useSelector(getIsLite);
  const isInSoftNotifyExperiment = useIsInSoftNotifyExperiment();
  const isMobile = useIsMobile();
  const shouldShowStatusBanner =
    useShouldShowStatusDataForExperiment() && !isMobile;
  const showRecentlyAddedBadge = useSelector(getShowRecentlyAddedBadge);
  const vehicleNotifyStatus = useVehicleNotifyStatus();

  return (
    <Wrapper
      className={classNames({ 'bg-white': isPartnerVehicle })}
      data-qa="hero-header-lite"
    >
      <FirstRow>
        <Tags />
      </FirstRow>
      <VehicleDescription>
        <YearMakeModel>
          {year} {make} {model}
        </YearMakeModel>
        <TrimMileageBadge>
          <span className="block m:inline">{trim}</span>
          <span className="hidden m:inline">•</span>
          <span data-qa="mileage">{mileage.toLocaleString()} miles</span>
          {!isInSoftNotifyExperiment && (
            <HeroBadge className="mt-8 m:ml-8 m:mt-0" />
          )}
        </TrimMileageBadge>
        {showRecentlyAddedBadge && isInSoftNotifyExperiment && (
          <RecentlyAddedBadge className="mt-4 rounded-full" />
        )}
      </VehicleDescription>
      <div>
        <PriceAndButtons>
          <div className="grid h-fit gap-y-4 text-right">
            <Pricing
              discount={totalDiscount}
              hasDiscount={hasDiscount}
              isLoading={isPriceLoading}
              isTradeInApplied={isTradeInApplied}
              previousPrice={previousPrice}
              price={price}
              tradeIncentive={tradeIncentive}
            />
          </div>
          {!notifyMe && !shouldShowStatusBanner && (
            <div className="hidden gap-8 m:flex">
              {shouldShowViewMoreCars && (
                <a
                  href={viewMoreCarsLink}
                  data-analytics-id="View More Cars"
                  data-analytics-attributes={JSON.stringify({
                    desktopSticky: false,
                    isMobileFooter: false,
                    status: vehicleNotifyStatus
                  })}
                >
                  <ViewMore>View More Cars</ViewMore>
                </a>
              )}
              <GetStarted
                className={isDisabled ? 'disabled' : undefined}
                data-analytics-id="Hero - CTA Button"
                data-qa="get-started-button"
              />
            </div>
          )}
        </PriceAndButtons>
      </div>
    </Wrapper>
  );
}

export default withErrorBoundary(HeroHeaderLite);
